import { FC, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import Caret from 'components/Icons/Caret';
import FullscreenOverlay from 'components/Others/FullscreenOverlay';
import useOnClickOutside from 'hooks/useOnClickOutside';
import useWindowSize from 'hooks/useWindowSize';

const MOBILE_VIEWPORT_WIDTH = 768;
interface OverlappingDropdownProps {
  value?: string | Number | JSX.Element;
  placeholder: string;
  active: boolean;
  // eslint-disable-next-line no-unused-vars
  updateActiveState: (value: boolean) => void;
  customHeight?: any;
  customWidth?: any;
}

const OverlappingDropdown: FC<OverlappingDropdownProps> = ({
  value,
  placeholder,
  children,
  active,
  updateActiveState,
  customHeight,
  customWidth
}) => {
  const toggleVisibility = () => {
    updateActiveState(!active);
  };

  return (
    <div className="relative">
      <div onClick={toggleVisibility}>
        <OverlappingDropdownToggle customHeight={customHeight}>
          <span className="mb-[4px] block font-medium text-brand-grey300">
            {value || placeholder}
          </span>
        </OverlappingDropdownToggle>
      </div>
      <OverlappingDropdownOverlay
        visible={active}
        toggleVisibility={() => updateActiveState(false)}
        customWidth={customWidth}
        placeholder={placeholder}>
        {children}
      </OverlappingDropdownOverlay>
    </div>
  );
};

interface OverlappingDropdownOverlayProps {
  visible: boolean;
  toggleVisibility: () => void;
  placeholder: string;
  customWidth?: any;
}

const OverlappingDropdownOverlay: FC<OverlappingDropdownOverlayProps> = ({
  children,
  ...overlayProps
}) => {
  const { width } = useWindowSize();

  return width > MOBILE_VIEWPORT_WIDTH ? (
    <OverlappingDropdownDeskopOverlay {...overlayProps}>
      {children}
    </OverlappingDropdownDeskopOverlay>
  ) : (
    <OverlappingDropdownMobileOverlay {...overlayProps}>
      {children}
    </OverlappingDropdownMobileOverlay>
  );
};

const OverlappingDropdownDeskopOverlay: FC<OverlappingDropdownOverlayProps> = ({
  visible,
  toggleVisibility,
  placeholder,
  children,
  customWidth
}) => {
  const perfectScrollbarInstance = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!perfectScrollbarInstance?.current) return;
    setTimeout(() => {
      perfectScrollbarInstance.current?.updateScroll?.();
    }, 500);
  }, [perfectScrollbarInstance]);

  useOnClickOutside(wrapperRef, toggleVisibility);

  return (
    <Transition
      show={visible}
      ref={wrapperRef}
      enter="transition ease-in-out duration-[450ms]"
      enterFrom="opacity-0 translate-y-[-25px]"
      enterTo="translate-y-0 opacity-1"
      leave="transition ease-in-out duration-[200ms] transform"
      leaveFrom="translate-y-0 opacity-1"
      leaveTo="translate-y-[-25px] opacity-0"
      className="absolute top-0 left-0 z-[2] min-h-full w-full rounded-[6px] border border-brand-grey350 bg-white py-[16px]" style={{width: customWidth}}>
      <div className="px-[16px]">
        <span
          className="flex cursor-pointer items-center justify-between border-b border-brand-grey500 pb-[14px] text-[14px]"
          onClick={toggleVisibility}>
          <span className="max-w-[60%] overflow-hidden overflow-ellipsis whitespace-nowrap leading-[18px]">
            {placeholder}
          </span>
          <span className="flex-shrink-0 rotate-180">
            <Caret />
          </span>
        </span>
      </div>
      <div className="mr-[6px] mt-[12px] max-h-[180px] overflow-auto">
        <div className="pl-[16px] pr-[10px]">{children}</div>
      </div>
    </Transition>
  );
};

const OverlappingDropdownMobileOverlay: FC<OverlappingDropdownOverlayProps> = ({
  visible,
  toggleVisibility,
  placeholder,
  children
}) => {
  return (
    <FullscreenOverlay
      isVisible={visible}
      title={placeholder}
      onToggleVisibility={toggleVisibility}>
      <div className="pr-[8px]">{children}</div>
    </FullscreenOverlay>
  );
};

const OverlappingDropdownToggle: FC<any> = ({ children, customHeight, customWidth}) => {
  return (
    <div className={"flex min-h-[40px] w-full cursor-pointer select-none items-center justify-between rounded-[6px] border border-brand-grey350 bg-white px-[16px] text-[14px]"} style={{height:customHeight}}>
      <span>{children}</span>
      <span>
        <Caret />
      </span>
    </div>
  );
};

export default OverlappingDropdown;